var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { generateUUID, findFirst, trimSlash, parseJson } from '@utils';
import { subscribeToChannel } from '@realtime/messagebus/messagebus';
import { executeAPICall } from './executionHelpers';
import { localize } from '@localisation/localizationUtils';
// Type Guards
export function isAction(val) {
    return !!val.model;
}
export function isService(val) {
    return !!val.app;
}
/**
 * Check if previous request should be cancelled
 */
export function cancelPreviousRequest(item, watchList) {
    let { id, options } = item;
    if (options && options.cancelPrevious && !!options.key) {
        if (id != watchList[options.key]) {
            console.log("cancelling request : ", id, " and waiting for : ", watchList[options.key]);
            return true;
        }
    }
    return false;
}
/**
 * Map a per-item batch error to a user-facing message.
 * 'NOT_AUTHORIZED' comes from the server when a role check refuses the item.
 */
function resolveItemError(error, language) {
    if (error === 'NOT_AUTHORIZED') {
        return localize(language || 'en', 'uxp-core.text.not-authorized');
    }
    return error;
}
/**
 * Subscribe to messagebus channel
 */
function subscribeToMessageBus(channel, callback, apiKey, lucyUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        yield subscribeToChannel({ apiKey, lucyUrl }, channel, callback);
    });
}
/**
 * Execute batched queue items
 */
export function executeBatchQueue(queue, lucyUrl, apiKey, watchList, loginUIRef, isPublicSession, language) {
    let payload = [];
    // subscribe to a channel to get response for each request
    // generate an id
    let channel = generateUUID();
    subscribeToMessageBus(channel, (message, channel) => {
        var _a;
        console.log('Message received for channel : ', channel);
        // parse payload
        const { parsed: payload, error: parseError } = parseJson(message);
        if (parseError) {
            console.warn('Unable to parse response payload', parseError);
            return 'done';
        }
        let { id, ok, error, data } = payload;
        let queueItem = findFirst(queue, q => q.id == id);
        console.log('response for request : ', id);
        if (!queueItem) {
            console.warn('Unable to find item', id, 'in', queue);
            return 'done';
        }
        if (ok) {
            if (isAction(queueItem) || isService(queueItem)) {
                // cancel previous requests
                if (cancelPreviousRequest(queueItem, watchList)) {
                    return 'canceled';
                }
                if ((_a = queueItem.options) === null || _a === void 0 ? void 0 : _a.json) {
                    const { parsed: jsonData, error: jsonError } = parseJson(data);
                    if (jsonError) {
                        queueItem.error('Error parsing data: ' + data);
                        return 'done';
                    }
                    data = jsonData;
                }
            }
            queueItem.callback(data);
        }
        else {
            queueItem.error(resolveItemError(error, language));
        }
        return 'done';
    }, apiKey, lucyUrl);
    for (var i in queue) {
        let qi = queue[i];
        if (isAction(qi)) {
            payload.push({ action: qi.action, model: qi.model, parameters: qi.parameters, id: qi.id });
        }
        else if (isService(qi)) {
            payload.push({ app: qi.app, service: qi.service, parameters: qi.parameters, id: qi.id });
        }
        else {
            payload.push({ collection: qi.collectionName, model: qi.modelName, parameters: qi.parameters, id: qi.id });
        }
    }
    let serviceName = 'ExecuteBatch';
    if (isPublicSession && !apiKey) {
        serviceName = 'AnonymousExecuteBatch';
    }
    console.log('Sending batched items', queue.length, payload.length);
    let url = trimSlash(lucyUrl) + `/api/UXP/${serviceName}`;
    let formData = new FormData();
    formData.set('payload', JSON.stringify(payload));
    formData.set('channel', channel);
    (() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const result = yield executeAPICall({
            method: "POST",
            url,
            data: formData,
            headers: {
                'Authorization': apiKey ? `APIKEY ${apiKey}` : '',
                'Content-Type': 'multipart/form-data'
            }
        });
        // Handle error from executeAPICall
        if (result.error) {
            const e = result.error;
            if (serviceName !== 'AnonymousExecuteBatch' && ((_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                // check if login UI is configured
                if (loginUIRef) {
                    (_b = loginUIRef.current) === null || _b === void 0 ? void 0 : _b.show();
                }
                // here we can redirect to the login page if required
            }
            // if error is cancel - ignore
            if (axios.isCancel(e))
                return;
            // else log
            console.log(e);
            for (var i in queue) {
                let q = queue[i];
                q === null || q === void 0 ? void 0 : q.error(e);
            }
            return;
        }
        // Process successful response
        console.log('response received ');
        let response = result.data;
        for (var i in response) {
            let item = response[i];
            let { id, ok, error, data } = item;
            let queueItem = findFirst(queue, q => q.id == id);
            if (!queueItem) {
                console.warn('Unable to find item', id, 'in', queue);
                continue;
            }
            if (ok) {
                if (isAction(queueItem) || isService(queueItem)) {
                    // cancel previous requests
                    if (cancelPreviousRequest(queueItem, watchList)) {
                        continue;
                    }
                    if ((_c = queueItem.options) === null || _c === void 0 ? void 0 : _c.json) {
                        const { parsed: jsonData, error: jsonError } = parseJson(data);
                        if (jsonError) {
                            queueItem.error('Error parsing data: ' + data);
                            continue;
                        }
                        data = jsonData;
                    }
                }
                queueItem.callback(data);
                continue;
            }
            queueItem.error(resolveItemError(error, language));
        }
    }))();
}
